import { SwipeableDrawer } from "@mui/material";
import dayjs from "dayjs";
import { useIntl } from "react-intl";

import { Button } from "@/components/Button/Button";
import { usePaymentContext } from "@/contexts/PaymentContext";
import { useLocaleInfo } from "@/hooks/useLocaleInfo";
import { formatCurrency } from "@/utils/currency";

interface InstallmentBreakdownDrawerProps {
  promoCodeDiscount?: number;
  totalPrice: number;
  open: boolean;
  onClose: () => void;
}

const InstallmentBreakdownDrawer = ({
  promoCodeDiscount,
  totalPrice,
  open,
  onClose,
}: InstallmentBreakdownDrawerProps) => {
  const intl = useIntl();
  const { locale } = useLocaleInfo();
  const { selectedInstallments } = usePaymentContext();

  const nextPaymentMonth = dayjs()
    .add(1, "month")
    .locale(locale)
    .format("MMMM");

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableRestoreFocus
    >
      <div className="pt-2">
        <div className="mb-6 text-center text-base font-medium">
          {intl.formatMessage({
            defaultMessage: "Instalment price breakdown",
            description: "instalmentBreakdown.title",
          })}
        </div>
        <div className="mb-6 flex flex-col gap-3 text-sm">
          <div className="flex justify-between">
            <span>
              {intl.formatMessage({
                defaultMessage: "Number of instalments",
                description: "instalmentBreakdown.numberOfInstalments",
              })}
            </span>
            <span className="font-bold">
              {intl.formatMessage(
                {
                  defaultMessage: "{count} months",
                  description: "instalmentBreakdown.months",
                },
                { count: selectedInstallments },
              )}
            </span>
          </div>
          <div className="flex justify-between">
            <span>
              {intl.formatMessage({
                defaultMessage: "Next payment",
                description: "instalmentBreakdown.nextPayment",
              })}
            </span>
            <span className="font-bold">{nextPaymentMonth}</span>
          </div>
          {promoCodeDiscount ? (
            <div className="flex justify-between">
              <span>
                {intl.formatMessage({
                  defaultMessage: "Promo code discount",
                  description: "promoCode.promoCodeDiscount",
                })}
              </span>
              <div className="font-bold text-core-accent-500">
                {formatCurrency(promoCodeDiscount)}
              </div>
            </div>
          ) : null}
          <div className="flex justify-between">
            <span>
              {intl.formatMessage({
                defaultMessage: "Monthly payment",
                description: "instalmentBreakdown.monthlyPayment",
              })}
            </span>
            <span className="font-bold">
              {formatCurrency(totalPrice / selectedInstallments)}
            </span>
          </div>
          <div className="flex justify-between">
            <span>
              {intl.formatMessage({
                defaultMessage: "Total",
                description: "total",
              })}
            </span>
            <span className="font-bold">{formatCurrency(totalPrice)}</span>
          </div>
        </div>

        <Button fullWidth variant="contained" onClick={onClose}>
          {intl.formatMessage({
            defaultMessage: "Close",
            description: "common.close",
          })}
        </Button>
      </div>
    </SwipeableDrawer>
  );
};

export default InstallmentBreakdownDrawer;
